import { useCallback, useEffect, useState } from "react";
import { Table, Tooltip } from "antd";
import { ArrowClockwise, Files, FolderUser, HardDrives, Users } from "@phosphor-icons/react";
import dayjs from "dayjs";
import useAuth from "../../../hooks/useAuth";
import { fetchPersonalStorageDashboard } from "../../../service/personalStorage";
import { formatFileSize } from "../../../utils/fileUtils";
import {
  Card,
  CardLabel,
  CardsRow,
  CardValue,
  EmptyState,
  Intro,
  SectionTitle,
  TableWrap,
  Wrap,
} from "./PersonalStorageDashboardStyled";

/**
 * "How are people using their personal spaces" — see the Personal Storage
 * Areas plan's Monitoring section. Restricted to the
 * `ops.personal_storage_dashboard` capability (Matthew's account only for
 * now, same allowlist mechanism as `ops.console`). Backend:
 * documents/views/personal.py PersonalStorageDashboardView.
 */
const PersonalStorageDashboard = () => {
  const { hasCapability } = useAuth();
  const canView = hasCapability("ops.personal_storage_dashboard");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchPersonalStorageDashboard();
      setData(response.data);
    } catch (err) {
      setError(
        err?.response?.status === 403
          ? "Your account no longer has access to this dashboard."
          : err?.response?.data?.detail || "Failed to load usage data."
      );
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    load();
  }, [load]);

  if (!canView) {
    return (
      <Wrap>
        <EmptyState data-testid="personal-storage-dashboard-restricted">
          <FolderUser size={40} color="#CBD5E1" />
          <p>This page is restricted.</p>
        </EmptyState>
      </Wrap>
    );
  }

  const usage = data?.usage_by_person || [];
  const totals = usage.reduce(
    (acc, row) => ({
      storage: acc.storage + (row.storage_bytes || 0),
      documents: acc.documents + (row.document_count || 0),
      folders: acc.folders + (row.folder_count || 0),
    }),
    { storage: 0, documents: 0, folders: 0 }
  );

  const usageColumns = [
    { title: "Person", dataIndex: "name", key: "name" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Storage used",
      dataIndex: "storage_bytes",
      key: "storage_bytes",
      align: "right",
      sorter: (a, b) => a.storage_bytes - b.storage_bytes,
      render: (value) => formatFileSize(value),
    },
    { title: "Files", dataIndex: "document_count", key: "document_count", align: "right" },
    { title: "Folders", dataIndex: "folder_count", key: "folder_count", align: "right" },
  ];

  const activityColumns = [
    {
      title: "When",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => (value ? dayjs(value).format("MMM D, YYYY h:mm A") : ""),
    },
    { title: "Action", dataIndex: "action_display", key: "action_display" },
    { title: "Whose area", dataIndex: "owner", key: "owner", render: (v) => v || "—" },
    { title: "Actor", dataIndex: "actor", key: "actor", render: (v) => v || "—" },
    {
      title: "Item",
      key: "item",
      render: (_, row) => row.document_name || row.folder_name || "—",
    },
  ];

  return (
    <Wrap>
      <Intro>
        Personal Storage usage across every person&apos;s My Files area —
        storage used, file counts, and recent activity. Visible only to your
        account today; the first candidate to move onto real roles once they
        exist.
        <Tooltip title="Refresh">
          <ArrowClockwise
            size={14}
            style={{ marginLeft: 8, cursor: "pointer" }}
            onClick={load}
          />
        </Tooltip>
      </Intro>

      {error && <EmptyState>{error}</EmptyState>}

      {!error && (
        <>
          <CardsRow>
            <Card>
              <CardLabel>
                <Users size={14} /> People with a personal area
              </CardLabel>
              <CardValue>{usage.length}</CardValue>
            </Card>
            <Card>
              <CardLabel>
                <HardDrives size={14} /> Total storage used
              </CardLabel>
              <CardValue>{formatFileSize(totals.storage) || "0 Bytes"}</CardValue>
            </Card>
            <Card>
              <CardLabel>
                <Files size={14} /> Total files
              </CardLabel>
              <CardValue>{totals.documents}</CardValue>
            </Card>
            <Card>
              <CardLabel>
                <FolderUser size={14} /> Total folders
              </CardLabel>
              <CardValue>{totals.folders}</CardValue>
            </Card>
          </CardsRow>

          <SectionTitle>Usage by person</SectionTitle>
          <TableWrap>
            <Table
              rowKey="user_id"
              columns={usageColumns}
              dataSource={usage}
              loading={loading}
              pagination={false}
              locale={{ emptyText: "No personal storage activity yet." }}
            />
          </TableWrap>

          <SectionTitle>Recent activity</SectionTitle>
          <TableWrap>
            <Table
              rowKey="id"
              columns={activityColumns}
              dataSource={data?.recent_activity || []}
              loading={loading}
              pagination={{ pageSize: 20 }}
              locale={{ emptyText: "No activity yet." }}
            />
          </TableWrap>
        </>
      )}
    </Wrap>
  );
};

export default PersonalStorageDashboard;
